import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import * as api from "../../services/api";
import { WIZARD_STEPS } from "./hooks/useWizardSteps";
import { useCurrentLocation } from "../LocationSelector.jsx";
import * as ws from "../../utils/ws";
import { THROTTLE_REFRESH } from "../../constants";
import { throttle } from "shared/src/util.mjs";
import { useAuthData } from "../../context/AuthenticationTokenContext";
import { logout } from "../../auth";
import { useSnackbarMessage } from "../Snackbar.jsx";
import { useNavigate } from "../../url";
import { useLoaderData, useRevalidator } from "react-router";
import { role } from "shared/src/roles.mjs";
import { dateInput } from "../../utils/formatter";
import { cleaningAppointmentSteps } from "shared/src/resource.mjs";

const WizardContext = createContext();
export const useWizardContext = () => {
  const ctx = useContext(WizardContext);
  if (!ctx)
    throw new Error("useWizardContext must be used within a WizardProvider");
  return ctx;
};

export function useCurrentAppointment() {
  const { clientData } = useWizardContext();

  const currentAppointmentId = clientData?.currentAppointmentId;
  const currentAppointment = (clientData?.todaysAppointments ?? []).find(
    (a) => a.id === currentAppointmentId,
  );

  return currentAppointment;
}

export function useCurrentAppointmentRequiresRtg() {
  const currentAppointment = useCurrentAppointment();

  const requires_rentgen = Boolean(
    currentAppointment?.service?.locationService?.requires_rentgen,
  );
  const calendar_types =
    currentAppointment?.service?.locationService?.actual_calendar?.types ?? [];

  return requires_rentgen && calendar_types.includes("other");
}

function isInterestingNotification(msg, clientId) {
  switch (msg.type) {
    case "__connect":
      return true;
    case "appointment_changed": {
      if (Object.keys(msg.payload).length === 0) {
        return true;
      }

      const { client_ids } = msg.payload;

      return client_ids?.includes(clientId);
    }
    default:
      return false;
  }
}

function useRefreshData(clientId) {
  const revalidator = useRevalidator();

  useEffect(() => {
    const refresh = throttle(() => {
      revalidator.revalidate();
    }, THROTTLE_REFRESH);

    function maybeRefresh(msg) {
      if (isInterestingNotification(msg, clientId)) {
        refresh(clientId);
      }
    }

    ws.subscribe(maybeRefresh);

    return () => ws.unsubscribe(maybeRefresh);
  }, [clientId]);
}

async function useMaybeLogout() {
  const navigate = useNavigate();
  const authData = useAuthData();
  const displaySnackbarMessage = useSnackbarMessage();
  const loaderData = useLoaderData();
  const { client } = loaderData;
  const appointmentId = client.currentAppointmentId;
  const currentAppointment = useMemo(
    () => (client.todaysAppointments ?? []).find((a) => a.id === appointmentId),
    [client.todaysAppointments, appointmentId],
  );
  const isCleaningMode = cleaningAppointmentSteps.includes(
    currentAppointment?.active_step,
  );

  useEffect(() => {
    (async function () {
      if (isCleaningMode) return;

      if (!currentAppointment?.employee?.id) {
        displaySnackbarMessage({
          title: "Došlo k vrácení klienta do čekárny.",
          autoHideDisabled: true,
          severity: "warning",
          content: "",
        });
        await logout({ navigate });
        return;
      }

      const apptChairId =
        currentAppointment.actual_calendar_id ||
        currentAppointment.calendar_id ||
        null;
      const myChairId =
        authData?.chair_id || localStorage.getItem("currentChairId") || null;

      if (
        myChairId &&
        apptChairId &&
        String(myChairId) !== String(apptChairId)
      ) {
        displaySnackbarMessage({
          title: "Došlo k vrácení klienta do čekárny.",
          autoHideDisabled: true,
          severity: "warning",
          content: "",
        });
        await logout({ navigate });
        return;
      }

      if (authData.id !== currentAppointment.employee.id) {
        displaySnackbarMessage({
          title: "Došlo k odhlášení kvůli změně přiřazeného personálu.",
          autoHideDisabled: true,
          severity: "warning",
          content: "",
        });
        await logout({ navigate });
        return;
      }
    })();
  }, [authData, currentAppointment, navigate, displaySnackbarMessage]);
}

function toMinimal(e) {
  if (!e) return null;
  return {
    id: e.id ?? null,
    name:
      [e.first_name, e.last_name].filter(Boolean).join(" ") || e.name || null,
    username: e.username ?? null,
    uzis_id: e.uzis_id ?? null,
  };
}

const ensureArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

function useHandleFormSubmit(
  currentAppointmentId,
  filledFormsData,
  selectedNurses,
  selectedDoctors,
  selectedCoordinators,
) {
  return React.useCallback(
    async function () {
      if (!currentAppointmentId) {
        console.error("No appointment ID found");
        return false;
      }
      try {
        for (const [templateId, filledData] of Object.entries(
          filledFormsData,
        )) {
          await api.createFilledServiceTemplate({
            appointment_id: currentAppointmentId,
            template_id: templateId,
            filled_data: filledData,
            selected_roles: {
              [role.Dental_Assistant]: selectedNurses.map((e) => e.id),
              [role.Doctor]: selectedDoctors.map((e) => e.id),
              [role.Treatment_Coordinator]: selectedCoordinators.map(
                (e) => e.id,
              ),
            },
            day: dateInput(new Date()),
          });
        }
        return true;
      } catch (error) {
        console.error("Error submitting forms:", error);
        return false;
      }
    },
    [
      currentAppointmentId,
      filledFormsData,
      selectedNurses,
      selectedDoctors,
      selectedCoordinators,
    ],
  );
}

export function stepToActiveStep(step) {
  return Object.keys(WIZARD_STEPS).find((k) => WIZARD_STEPS[k] === step);
}

export const WizardProvider = ({ children }) => {
  const location_id = useCurrentLocation();

  const [activeStep, _setActiveStep] = useState(WIZARD_STEPS.PREPARATION);
  const [clientData, setClientData] = useState(null);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [filledFormsData, setFilledFormsData] = useState({});
  const [appointmentStatus, setAppointmentStatus] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [nextAppointmentNote, setNextAppointmentNote] = useState("");
  const [floorManagerNotes, setFloorManagerNotes] = useState("");
  const [isTemplateFormValid, setIsTemplateFormValid] = useState(false);
  const [displayFullInfo, setDisplayFullInfo] = useState(false);
  const [carryOverNextAppointmentNote, setCarryOverNextAppointmentNote] =
    useState(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const [rtgNotRequired, setRtgNotRequired] = useState(false);
  const [rtgBypassReason, setRtgBypassReason] = useState("");
  const [appointmentNeeded, setAppointmentNeeded] = useState("");
  const [appointmentNeededTouched, setAppointmentNeededTouched] =
    useState(false);

  const [selectedCoordinators, setSelectedCoordinators] = useState([]);
  const [selectedDoctors, setSelectedDoctors] = useState([]);
  const [selectedNurses, setSelectedNurses] = useState([]);

  const [
    isRtgMandatoryForCurrentAppointment,
    setIsRtgMandatoryForCurrentAppointment,
  ] = useState(false);

  useMaybeLogout();

  const currentAppointmentId = clientData?.currentAppointmentId || null;

  const _hydrateActiveStep = useCallback((step) => {
    _setActiveStep(step);
  }, []);

  const setActiveStep = useCallback(
    async (step) => {
      _setActiveStep(step);
      if (!currentAppointmentId) return;
      try {
        const stepEnum = stepToActiveStep(step);
        if (!stepEnum) return;

        const appt = await api.loadAppointments({
          filter: { id: { eq: currentAppointmentId } },
          location_id,
        });
        const existing =
          appt?.data?.appointments?.data?.[0]?.active_step || null;
        if (existing !== stepEnum) {
          await api.patchAppointment({
            id: currentAppointmentId,
            active_step: stepEnum,
          });
        }
      } catch (err) {
        console.error("Failed to patch active step:", err);
      }
    },
    [currentAppointmentId, location_id],
  );

  useEffect(() => {
    const initializeWizardState = async () => {
      if (!currentAppointmentId) return;
      const today = new Date();
      const startOfDay = new Date(today.setHours(0, 0, 0, 0)).toISOString();
      const resp = await api.loadAppointments({
        filter: {
          id: { eq: currentAppointmentId },
          start: { gte: startOfDay },
        },
        location_id,
      });
      const appt = resp?.data?.appointments?.data?.[0];
      if (appt) {
        setAppointmentStatus(appt.status_id);
        const stepKey = appt.active_step;
        const stepValue = WIZARD_STEPS[stepKey];
        _hydrateActiveStep(stepValue ?? WIZARD_STEPS.PREPARATION);

        if (appt.employee && appt.employee.id) {
          const nurse = toMinimal({
            id: appt.employee.id,
            first_name: appt.employee.first_name,
            last_name: appt.employee.last_name,
            username: appt.employee.username ?? null,
            uzis_id: appt.employee.uzis_id ?? null,
          });
          setSelectedNurses((prev) =>
            prev?.length ? prev : nurse ? [nurse] : [],
          );
        }
      }
    };
    initializeWizardState();
  }, [currentAppointmentId, _hydrateActiveStep, location_id]);

  const handleFormSubmit = useHandleFormSubmit(
    currentAppointmentId,
    filledFormsData,
    selectedNurses,
    selectedDoctors,
    selectedCoordinators,
  );

  useRefreshData(clientData?.id);

  const rtgCreated = useMemo(
    () =>
      clientData?.todaysAppointments?.some((a) => {
        const type = a.service?.type;
        const isRTG = type === "rentgen";
        const isActive = a.status_id !== 100;
        return isRTG && isActive;
      }) ?? false,
    [clientData],
  );

  const coordinatorIds = useMemo(
    () =>
      ensureArray(selectedCoordinators)
        .map((e) => e?.id)
        .filter(Boolean),
    [selectedCoordinators],
  );
  const doctorIds = useMemo(
    () =>
      ensureArray(selectedDoctors)
        .map((e) => e?.id)
        .filter(Boolean),
    [selectedDoctors],
  );
  const nurseIds = useMemo(
    () =>
      ensureArray(selectedNurses)
        .map((e) => e?.id)
        .filter(Boolean),
    [selectedNurses],
  );

  const contextValue = useMemo(
    () => ({
      activeStep,
      setActiveStep,
      clientData,
      setClientData,
      currentAppointmentId,
      setFilledFormsData,
      filledFormsData,
      handleFormSubmit,
      selectedServiceId,
      setSelectedServiceId,
      elapsedTime,
      setElapsedTime,
      appointmentStatus,
      nextAppointmentNote,
      setNextAppointmentNote,
      rtgCreated,
      floorManagerNotes,
      setFloorManagerNotes,
      isTemplateFormValid,
      setIsTemplateFormValid,
      displayFullInfo,
      setDisplayFullInfo,
      carryOverNextAppointmentNote,
      setCarryOverNextAppointmentNote,
      isCompleting,
      setIsCompleting,
      rtgNotRequired,
      setRtgNotRequired,
      rtgBypassReason,
      setRtgBypassReason,
      appointmentNeeded,
      setAppointmentNeeded,
      appointmentNeededTouched,
      setAppointmentNeededTouched,
      selectedCoordinators,
      setSelectedCoordinators,
      selectedDoctors,
      setSelectedDoctors,
      selectedNurses,
      setSelectedNurses,
      coordinatorIds,
      doctorIds,
      nurseIds,
      isRtgMandatoryForCurrentAppointment,
      setIsRtgMandatoryForCurrentAppointment,
      _setActiveStep,
    }),
    [
      activeStep,
      setActiveStep,
      clientData,
      setClientData,
      currentAppointmentId,
      setFilledFormsData,
      filledFormsData,
      handleFormSubmit,
      selectedServiceId,
      setSelectedServiceId,
      elapsedTime,
      setElapsedTime,
      appointmentStatus,
      nextAppointmentNote,
      setNextAppointmentNote,
      rtgCreated,
      floorManagerNotes,
      setFloorManagerNotes,
      isTemplateFormValid,
      setIsTemplateFormValid,
      displayFullInfo,
      setDisplayFullInfo,
      carryOverNextAppointmentNote,
      setCarryOverNextAppointmentNote,
      isCompleting,
      setIsCompleting,
      rtgNotRequired,
      setRtgNotRequired,
      rtgBypassReason,
      setRtgBypassReason,
      appointmentNeeded,
      setAppointmentNeeded,
      appointmentNeededTouched,
      setAppointmentNeededTouched,
      selectedCoordinators,
      selectedDoctors,
      selectedNurses,
      coordinatorIds,
      doctorIds,
      nurseIds,
      isRtgMandatoryForCurrentAppointment,
      _setActiveStep,
    ],
  );

  return (
    <WizardContext.Provider value={contextValue}>
      {children}
    </WizardContext.Provider>
  );
};
